import type { CompanyProfilePersonDto } from '@/api/companies'
import type { ColumnConfig } from '@/components/common/grids/DataGrid'
import { PersonAvatar } from '@/components/common/Avatar'
import { SignalBars } from '@/components/common/SignalBars'
import { timeFromNowVerbose } from '@/utils/time'
import { CompanyPersonStatus } from './CompanyPersonStatus'

/**
 * The roster's columns (ORB-623). Split out of CompanyProfilePage so that file is composition and
 * this one is presentation — the page was doing fetching, routing, filtering and grid config at once.
 */
export function companyPeopleColumns(): ColumnConfig<CompanyProfilePersonDto>[] {
  return [
    {
      key: 'person',
      header: 'Person',
      sortable: true,
      required: true,
      accessor: p => p.FullName || p.Email || '',
      render: p => (
        <div class="flex items-center gap-3">
          <PersonAvatar fullName={p.FullName} profilePicUrl={p.ProfilePicUrl} size="sm" />
          <div class="min-w-0">
            <div class="font-medium text-slate-800 dark:text-slate-100 truncate">
              {p.FullName || p.Email || 'Unknown'}
            </div>
            {p.JobTitle && (
              <div class="text-xs text-slate-500 dark:text-slate-400 truncate">{p.JobTitle}</div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      accessor: p => p.Status,
      render: p => <CompanyPersonStatus status={p.Status} />,
    },
    {
      key: 'signal',
      header: 'Signal',
      sortable: true,
      accessor: p => p.SignalScore,
      // Bars only where there is signal to show. A targeted person has none by definition, and an
      // empty five-bar frame reads as "scored zero" rather than "not scored".
      render: p =>
        p.Status === 'Converted'
          ? <SignalBars score={p.SignalScore} class="flex items-end h-5 w-14 gap-1" />
          : <span class="text-slate-400 dark:text-slate-500">—</span>,
    },
    {
      key: 'campaigns',
      header: 'Campaigns',
      sortable: true,
      accessor: p => p.CampaignCount,
      render: p => <span class="tabular-nums">{p.CampaignCount}</span>,
    },
    {
      key: 'lastActivity',
      header: 'Last activity',
      sortable: true,
      accessor: p => p.LastActivityUtc || '',
      render: p =>
        p.LastActivityUtc
          ? <span class="text-xs text-slate-600 dark:text-slate-300">{timeFromNowVerbose(p.LastActivityUtc)}</span>
          : <span class="text-xs text-slate-400 dark:text-slate-500">Never</span>,
    },
  ]
}
